import type { AdditionalCostType } from '@api'
import { AdditionalCostTypeRestEndpointService } from '@api'
import { useMutation, useQueryClient } from 'react-query'

import { useToast } from '@/components/ui/toast'
import { handleError } from '@/utils/error'

export const useAccountingAdditionCostTypeMutations = () => {
  const queryClient = useQueryClient()
  const toast = useToast()

  const createCostType = useMutation({
    mutationFn: (body: { name: string }) =>
      AdditionalCostTypeRestEndpointService.dashboardCreateAdditionalCostType({
        requestBody: body,
      }),
    onSuccess: () => {
      toast.success('Cost type created successfully')
      queryClient.invalidateQueries({
        queryKey: ['accounting', 'additional-cost', 'types'],
      })
    },
    onError: (error) => {
      handleError(error, 'Something went wrong while creating cost type')
    },
  })

  const deleteCostType = useMutation({
    mutationFn: (id: number) =>
      AdditionalCostTypeRestEndpointService.dashboardDeleteAdditionalCostType({
        typeId: id,
      }),
    onSuccess: (_, id) => {
      toast.success('Cost type deleted successfully')
      const data = queryClient.getQueryData<AdditionalCostType[]>([
        'accounting',
        'additional-cost',
        'types',
      ])

      queryClient.setQueryData(
        ['accounting', 'additional-cost', 'types'],
        data?.filter((item) => item.id !== id),
      )
    },
    onError: (error) => {
      handleError(error, 'Something went wrong while updating cost type')
    },
  })

  const updateCostType = useMutation({
    mutationFn: (variables: { id: number; name: string }) =>
      AdditionalCostTypeRestEndpointService.dashboardUpdateAdditionalCostType({
        requestBody: {
          name: variables.name,
        },
        typeId: variables.id,
      }),
    onSuccess: () => {
      toast.success('Cost type updated successfully')
      queryClient.invalidateQueries({
        queryKey: ['accounting', 'additional-cost', 'types'],
      })
    },
    onError: (error) => {
      handleError(error, 'Something went wrong while deleting cost type')
    },
  })

  return {
    createCostType,
    deleteCostType,
    updateCostType,
  }
}
